import { Dataset } from 'app/model/dataset/dataset';
import { BinaryDataset } from 'app/model/dataset/binary.dataset';
import { Metamodel } from 'app/model/base/model';
import { AzureDataLakeStoreFileDataset } from 'app/model/dataset/azure-data-lake-store.dataset';
import { AzureBlobFSDataset } from 'app/model/dataset/azure-blob-fs.dataset';
import { AzureDataLakeLocation } from 'app/model/dataset/location/azure-datalake.location';
import { AzureBlobFSLocation } from 'app/model/dataset/location/azure-blobfs.location';
import { AzureBlobStorageLocation } from 'app/model/dataset/location/azure-blob.location';
import { AmazonS3Location } from 'app/model/dataset/location/amazon-s3.location';
import { CopyActivity } from 'app/model/pipeline/copy.activity';

import { SqlDWSink } from 'app/model/pipeline/copysink/sqldw.copysink';
import { AzureFileStorageLinkedService } from 'app/model/linkedservice/azure-file-storage.linkedservice';
import { azureStorageAuthTypes, LakehouseTableDataset, LinkedService, ParquetDataset, SnowflakeSink } from 'shell-models-lib';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { preserveRulesOptions, preserveRulesAttributesOption, preserveRulesTimeOption as preserveRulesDatetimeOption } from '../constants/copy-performance-setting.constant';
import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { compareDetailVersion } from './version.check.util';

export interface IWarningMessage {
    sinkPropertyName: string;
    sinkDetailedMessage: string;
}

export function hasSnowV2ScriptParam(version: string): boolean {
    // SnowflakeV2 version 1.1 supports script parameter
    return compareDetailVersion(version, '1.1') >= 0;
}

export function supportPreserveRules(sourceDataset: Dataset, sinkDataset: Dataset): boolean {
    return supportPreserveNonAttributesRules(sourceDataset, sinkDataset) || supportPreserveAttributes(sourceDataset, sinkDataset) || supportPreserveDatetime(sourceDataset, sinkDataset);
}

export function supportPreserveNonAttributesRules(sourceDataset: Dataset, sinkDataset: Dataset): boolean {
    return (sourceDataset != null
        && Metamodel.modelOf(sourceDataset).name === Metamodel.modelFor(AzureDataLakeStoreFileDataset).name
        && sinkDataset != null
        && Metamodel.modelOf(sinkDataset).name === Metamodel.modelFor(AzureBlobFSDataset).name)
        ||
        (sourceDataset instanceof BinaryDataset &&
            sinkDataset instanceof BinaryDataset &&
            !sourceDataset.compressionCodec && !BinaryDataset.COMPRESSION_CODEC.templateValue(sourceDataset) &&
            !sinkDataset.compressionCodec && !BinaryDataset.COMPRESSION_CODEC.templateValue(sinkDataset) &&
            (sourceDataset.location instanceof AzureDataLakeLocation ||
                (sourceDataset.location instanceof AzureBlobFSLocation)) &&
            sinkDataset.location instanceof AzureBlobFSLocation);
}

export function generatePreserveRulesOptions(sourceDataset: Dataset, sinkDataset: Dataset): Array<ISelectOption<string>> {
    const result: Array<ISelectOption<string>> = [];
    if (supportPreserveNonAttributesRules(sourceDataset, sinkDataset)) {
        result.push(...preserveRulesOptions);
    }
    if (supportPreserveAttributes(sourceDataset, sinkDataset)) {
        result.push(preserveRulesAttributesOption);
    }
    if (supportPreserveDatetime(sourceDataset, sinkDataset)) {
        result.push(...preserveRulesDatetimeOption);
    }
    return result;
}

function isNewAzureFileResource(linkedService: LinkedService): boolean {
    return linkedService instanceof AzureFileStorageLinkedService && !linkedService.fullyByJsonEditor &&
        (linkedService.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication || linkedService.authenticationMethod === azureStorageAuthTypes.SasUriAuthentication);
}

export function supportPreserveAttributes(sourceDataset: Dataset, sinkDataset: Dataset): boolean {
    return sourceDataset instanceof BinaryDataset && sinkDataset instanceof BinaryDataset &&
        !sourceDataset.compressionCodec && !BinaryDataset.COMPRESSION_CODEC.templateValue(sourceDataset) &&
        !sinkDataset.compressionCodec && !BinaryDataset.COMPRESSION_CODEC.templateValue(sinkDataset) &&
        (sourceDataset.location instanceof AzureBlobStorageLocation || sourceDataset.location instanceof AmazonS3Location
            || sourceDataset.location instanceof AzureBlobFSLocation || isNewAzureFileResource(sourceDataset.linkedService)) &&
        (sinkDataset.location instanceof AzureBlobStorageLocation || sinkDataset.location instanceof AzureBlobFSLocation || isNewAzureFileResource(sinkDataset.linkedService));
}

export function supportPreserveDatetime(sourceDataset: Dataset, sinkDataset: Dataset): boolean {
    return GlobalVariables.supportPreserveDatetime && sourceDataset instanceof BinaryDataset && sinkDataset instanceof BinaryDataset &&
        !sourceDataset.compressionCodec && !BinaryDataset.COMPRESSION_CODEC.templateValue(sourceDataset) &&
        !sinkDataset.compressionCodec && !BinaryDataset.COMPRESSION_CODEC.templateValue(sinkDataset) &&
        sourceDataset.location instanceof AzureBlobFSLocation &&
        sinkDataset.location instanceof AzureDataLakeLocation;
}

export function generateFaultToleranceWarningForCommonCopy(copyActivity: CopyActivity): IWarningMessage {
    if (copyActivity?.faultTolerenceSettings?.enableSkipIncompatibleRow) {
        if (copyActivity.copySink instanceof SnowflakeSink && copyActivity.copySink.importSettings
            && (!copyActivity.copySink.importSettings.additionalCopyOptions || (copyActivity.copySink.importSettings.additionalCopyOptions.find((item) => item.name === 'ON_ERROR') == null))) {
            return {
                sinkPropertyName: format(stringResources.Additional_CopyOptions_Label, stringResources.Snowflake_DisplayText),
                sinkDetailedMessage: stringResources.FaultToleranceWarningInSettingsConfigure + stringResources.FaultToleranceWarningInSettingsForSnowflake
            };
        } else if (CopyActivity.checkIfEnabledPolybase(copyActivity)) {
            return {
                sinkPropertyName: stringResources.PolybaseSetting_Polybase_Label,
                sinkDetailedMessage: stringResources.FaultToleranceWarningInSettingsConfigure + stringResources.FaultToleranceWarningInSettingsForPolybase
            };
        }
    }
    return undefined;
}

export function generateFaultToleranceWarningForStagedCopy(copyActivity: CopyActivity): string {
    if (copyActivity?.enableStaging && copyActivity.faultTolerenceSettings && !copyActivity.faultTolerenceSettings.enableSkipIncompatibleRow) {
        if ((copyActivity.copySink instanceof SnowflakeSink && copyActivity.copySink.importSettings?.additionalCopyOptions?.find((item) => item.name === 'ON_ERROR') != null) ||
            (CopyActivity.checkIfEnabledCopyCommand(copyActivity) && copyActivity.copySink instanceof SqlDWSink
                && copyActivity.copySink.copyCommandSettings?.additionalOptions?.find((item) => item.name === 'MAXERRORS') != null)) {
            return stringResources.FaultToleranceWarningInSettingsForStagedCopy;
        }
    }
    return undefined;
}

export function isAdaptivePerformanceTuningSupported(copyActivity: CopyActivity): boolean {
    return __IS_FABRIC_DPE__ && (
        (copyActivity.sourceDataset instanceof LakehouseTableDataset && copyActivity.sinkDataset instanceof ParquetDataset) ||
        (copyActivity.sourceDataset instanceof LakehouseTableDataset && copyActivity.sinkDataset instanceof LakehouseTableDataset) ||
        (copyActivity.sourceDataset instanceof ParquetDataset && copyActivity.sinkDataset instanceof LakehouseTableDataset) ||
        (copyActivity.sourceDataset instanceof ParquetDataset && copyActivity.sinkDataset instanceof ParquetDataset)
    );
}
